import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import ScopeMatrix from './ScopeMatrix';
import { errorText } from '../../lib/apiErrors';
import {
    EXPIRY_OPTIONS,
    PRESETS,
    createKey,
    presetFor,
    scopeSentence,
    sourceTagFor,
} from '../../lib/developerApi';

const INPUT = 'h-9 w-full px-3 border border-stone-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/20';

function emptyDraft(defaultOwner) {
    return {
        name: '',
        scopes: [...PRESETS[1].scopes],
        custom: false,
        ownerId: defaultOwner || '',
        source: '',
        sourceEdited: false,
        expiry: 'Never',
        ips: '',
    };
}

/**
 * Create an API key: name, a preset or a custom scope matrix, the default owner
 * for leads that name no teammate, a source tag, expiry and an optional IP
 * allowlist. On success hands the response — which carries the raw key, once —
 * to `onCreated`, which shows it in `RevealKeyDialog`.
 */
export default function CreateKeyDialog({ open, onOpenChange, team, defaultOwnerId, headers, onCreated }) {
    const [draft, setDraft] = useState(() => emptyDraft(defaultOwnerId));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Reset when the dialog opens — not when `team` arrives or is refetched
    // while it is open, which would wipe what the admin is typing. A default
    // owner that arrives late fills an empty owner only.
    const wasOpen = useRef(false);
    useEffect(() => {
        if (open && !wasOpen.current) {
            setDraft(emptyDraft(defaultOwnerId || team?.[0]?.user_id));
            setError('');
        } else if (open) {
            const fallback = defaultOwnerId || team?.[0]?.user_id;
            if (fallback) setDraft((d) => (d.ownerId ? d : { ...d, ownerId: fallback }));
        }
        wasOpen.current = open;
    }, [open, defaultOwnerId, team]);

    const preset = draft.custom ? 'custom' : presetFor(draft.scopes);
    const source = draft.sourceEdited ? draft.source : sourceTagFor(draft.name);
    const canCreate = draft.name.trim() && draft.scopes.length && draft.ownerId && !saving;
    const hint = !draft.name.trim()
        ? 'Give the key a name to continue.'
        : !draft.scopes.length
            ? 'Pick at least one permission.'
            : !draft.ownerId
                ? 'Pick a default owner.'
                : 'The key is shown once, right after you create it.';

    const owners = useMemo(() => team || [], [team]);

    const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

    const submit = async () => {
        if (!canCreate) return;
        setSaving(true);
        setError('');
        const days = EXPIRY_OPTIONS.find((o) => o.label === draft.expiry)?.days;
        const body = {
            name: draft.name.trim(),
            scopes: draft.scopes,
            default_owner_id: draft.ownerId,
            source_tag: source,
            ip_allowlist: draft.ips.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
        };
        if (days) body.expires_in_days = days;
        try {
            const created = await createKey(headers, body);
            onCreated(created);
        } catch (err) {
            const message = errorText(err, 'Could not create the key.');
            setError(message);
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-key-dialog">
                <DialogHeader>
                    <DialogTitle>Create API key</DialogTitle>
                    <DialogDescription>The key is shown once, right after you create it.</DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-5">
                    <label className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-stone-900">Name</span>
                        <input
                            className={INPUT}
                            data-testid="key-name"
                            value={draft.name}
                            maxLength={80}
                            placeholder="e.g. Website contact form"
                            onChange={(e) => set({ name: e.target.value })}
                        />
                        <span className="text-xs text-stone-500">Shown as the author of every record this key writes.</span>
                    </label>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-stone-900">Start from</span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    data-testid={`preset-${p.id}`}
                                    aria-pressed={preset === p.id}
                                    onClick={() => set(p.scopes ? { scopes: [...p.scopes], custom: false } : { custom: true })}
                                    className={`flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2.5 text-left ${preset === p.id ? 'border-stone-900 ring-1 ring-stone-900' : 'border-stone-200 hover:bg-stone-50'}`}
                                >
                                    <b className="text-[13px] font-semibold">{p.title}</b>
                                    <small className="text-[11.5px] text-stone-500 leading-snug">{p.description}</small>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-stone-900">Permissions</span>
                        <ScopeMatrix value={draft.scopes} onChange={(scopes) => set({ scopes, custom: presetFor(scopes) === 'custom' })} />
                        <div className="text-[13px] text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5" data-testid="scope-sentence">
                            {scopeSentence(draft.scopes)}
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium text-stone-900">Default owner</span>
                            <select
                                className={INPUT}
                                data-testid="key-owner"
                                value={draft.ownerId}
                                onChange={(e) => set({ ownerId: e.target.value })}
                            >
                                <option value="">Choose a teammate…</option>
                                {owners.map((m) => (
                                    <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                                ))}
                            </select>
                            <span className="text-xs text-stone-500">Gets leads that don’t name a teammate.</span>
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium text-stone-900">Source tag</span>
                            <input
                                className={`${INPUT} font-mono text-[13px]`}
                                data-testid="key-source"
                                value={source}
                                maxLength={60}
                                onChange={(e) => set({ source: e.target.value, sourceEdited: true })}
                            />
                            <span className="text-xs text-stone-500">Filter campaigns and lists by where a lead came from.</span>
                        </label>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium text-stone-900">Expires</span>
                            <div className="flex items-center gap-1 p-1 bg-stone-100 rounded-lg">
                                {EXPIRY_OPTIONS.map((o) => (
                                    <button
                                        key={o.label}
                                        type="button"
                                        aria-pressed={draft.expiry === o.label}
                                        onClick={() => set({ expiry: o.label })}
                                        className={`flex-1 h-7 rounded-md text-[13px] font-medium whitespace-nowrap px-2 ${draft.expiry === o.label ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600'}`}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium text-stone-900">
                                Allowed IPs <span className="font-normal text-stone-400">optional</span>
                            </span>
                            <textarea
                                className="h-16 px-3 py-2 border border-stone-200 rounded-md font-mono text-xs resize-none focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                data-testid="key-ips"
                                value={draft.ips}
                                placeholder="One IP or CIDR range per line. Empty means any IP."
                                onChange={(e) => set({ ips: e.target.value })}
                            />
                        </label>
                    </div>

                    {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" role="alert">{error}</div>}
                </div>

                <DialogFooter className="items-center gap-2 sm:justify-between">
                    <span className="text-[12.5px] text-stone-500" data-testid="create-hint">{hint}</span>
                    <div className="flex gap-2">
                        <button type="button" className="h-9 px-4 rounded-md border border-stone-200 text-sm font-medium hover:bg-stone-50" onClick={() => onOpenChange(false)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            data-testid="create-key-submit"
                            disabled={!canCreate}
                            onClick={submit}
                            className="h-9 px-4 rounded-md bg-stone-900 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                            Create key
                        </button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
